// react
import { ReactNode, SyntheticEvent, useContext, useState } from 'react';

// types
import { Loan, Pagination, Project, ProjectPayload, Transaction } from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// context
import { ContextBanner } from '../../../App.context';
import {
	ContextActivities,
	ContextLoans,
	ContextProject,
	ContextUserProjectPermissions,
} from '../../pages/Project/Project.context';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { ChipUtilization } from '../../ui/ChipUtilization/ChipUtilization';
import { IconCircleLoan } from '../../ui/IconCircleLoan/IconCircleLoan';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// dialogs
import { DialogEnableLoan } from '../../dialogs/DialogEnableLoan/DialogEnableLoan';
import { DialogOnboardLoan } from '../../dialogs/DialogOnboardLoan/DialogOnboardLoan';

// icons
import {
	AccountBalanceOutlined as IconAccountBalance,
	AssignmentOutlined as IconAssignment,
	AssignmentTurnedInOutlined as IconAssignmentTurnedIn,
	EditOutlined as IconEdit,
	LocalAtmOutlined as IconLocalAtm,
	PaidOutlined as IconPaid,
	SwapHorizOutlined as IconSwapHoriz,
} from '@mui/icons-material';

// utils
import { defaultPagination } from '../../../utils/defaults';
import { TableBasicTransactions } from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';
import { animatedPulse } from '../../../utils/animations';

// theme
import { theme } from '../../../theme';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface RenderKeyValueParams {
	label: string;
	value: ReactNode;
}

export interface CardLoanProps {
	currency?: string;
	loan: Loan;
	project: Project;
}

export const CardLoan = ({ currency, loan, project }: CardLoanProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();

	// state
	const [isExpanded, setIsExpanded] = useState(false);
	const [paginationTransactions, setPaginationTransactions] =
		useState<Pagination>(defaultPagination);
	const [searchParams, setSearchParams] = useSearchParams();
	const [tabValue, setTabValue] = useState('transactions');
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// context
	const { refetchActivities } = useContext(ContextActivities);
	const { setBanner } = useContext(ContextBanner);
	const { refetchLoans } = useContext(ContextLoans);
	const { isOnboarded: isProjectOnboarded } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogLoan = searchParams.get('dialogLoan');

	// fetch transactions
	const urlFetchTransactions = `${process.env.REACT_APP_API_URL}/transactions?loan=${loan.id}&page=1&limit=5&populate=expenseType&orderBy=date&order=DESC`;

	const { isLoading: isLoadingTransactions } = useFetch({
		isEnabled: Boolean(loan.id),
		url: urlFetchTransactions,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}

			if (res.pagination) {
				setPaginationTransactions(res.pagination);
			}
		},
	});

	// vars
	const isDisabled = loan.status === 'DISABLED';
	const isExtFinancing = financiersExt.some((f) => loan.financier === f);

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleOnboardLoan = async (loan: Loan) => {
		searchParams.set('dialog', 'onboardLoan');
		searchParams.set('dialogLoan', `${loan.code}`);
		setSearchParams(searchParams);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogLoan');
		setSearchParams(searchParams);
	};

	const handleOnboardingSuccess = () => {
		// refetch loans
		refetchLoans();

		// set onboard success banner
		setBanner({
			hasClose: true,
			message: t('alert.onboardSuccessLoan'),
			severity: 'success',
		});
	};

	const tabs = [
		{
			label: t('recentTransactions'),
			value: 'transactions',
		},
	];

	const btnMenuItems: MenuItemProps[] = [];

	// Add edit option for GOV and OTH loans only
	if (isExtFinancing && userProjectPermissions?.loan?.update && !loan.isNew && !isDisabled) {
		btnMenuItems.push({
			children: t('editFinancingSource'),
			icon: <IconEdit />,
			onClick: () => {
				navigate(
					`/project/${project.idWbg}/financing?dialog=editFinancingSource&dialogLoan=${loan.id}`
				);
			},
		});
	}

	if (userProjectPermissions?.entity?.create) {
		btnMenuItems.push({
			children: t('createEntity'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconAccountBalance />,
			to: `/project/${project.idWbg}/entities?dialog=addEntity`,
		});
	}

	if (userProjectPermissions?.payment?.create) {
		btnMenuItems.push({
			children: t('recordPayment'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconPaid />,
			to: `/project/${project?.idWbg}/transactions/payments?dialog=recordPayment`,
		});
	}
	if (userProjectPermissions?.cashBenefit?.create) {
		btnMenuItems.push({
			children: t('recordCashBenefit'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconPaid />,
			to: `/project/${project?.idWbg}/transactions/cash-benefits?dialog=recordCashBenefit`,
		});
	}
	if (userProjectPermissions?.opEx?.create) {
		btnMenuItems.push({
			children: t('recordOpEx'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconLocalAtm />,
			to: `/project/${project?.idWbg}/transactions/opex?dialog=recordOpEx`,
		});
	}
	if (userProjectPermissions?.transfer?.create) {
		btnMenuItems.push({
			children: t('recordFundTransfer'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconSwapHoriz />,
			to: `/project/${project?.idWbg}/transactions/transfers?dialog=recordFundTransfer`,
		});
	}

	if (userProjectPermissions?.contract?.create) {
		btnMenuItems.push({
			children: t('addContract'),
			component: RouterLink,
			disabled: isDisabled,
			icon: <IconAssignment />,
			to: `/project/${project.idWbg}/contracts?dialog=addContract`,
		});
	}

	if (isDisabled && userProjectPermissions?.loan?.update) {
		btnMenuItems.unshift({
			children: t('enableLoan'),
			disabled: project.status === 'PENDING',
			icon: <IconAssignmentTurnedIn color="success" />,
			onClick: () => {
				searchParams.set('dialog', 'enableLoan');
				searchParams.set('dialogLoan', String(loan.code));
				setSearchParams(searchParams);
			},
		});
	}

	let loanAmount: null | number = loan.amount;
	let loanCurrency: null | string = loan.currency;
	let loanDisbursed: null | number = loan.disbursed;
	let loanUndisbursed = null;

	if (currency && loan.currency !== currency) {
		const amountByCurrency = loan.conversions?.find(
			(item) => item.currency === currency && item.type === 'AMOUNT'
		);

		if (amountByCurrency) {
			loanAmount = amountByCurrency.amount;
			loanCurrency = amountByCurrency.currency;
			// Find disbursed conversion for same currency
			const disbursedByCurrency = loan.conversions?.find(
				(item) => item.currency === currency && item.type === 'DISBURSED'
			);
			loanDisbursed = disbursedByCurrency?.amount || null;
		} else {
			loanAmount = null;
			loanCurrency = null;
			loanDisbursed = null;
		}
	}

	if (typeof loanAmount === 'number' && typeof loanDisbursed === 'number') {
		loanUndisbursed = loanAmount - loanDisbursed;
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box
					sx={(theme) => {
						return {
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={2}>
							<Box
								sx={(theme) => {
									return {
										margin: theme.spacing(0, 0, 2, 0),
										[theme.breakpoints.up('lg')]: {
											margin: 0,
										},
									};
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircleLoan
											badges={
												loan.isNew
													? [
															{
																color: 'error',
																overlap: 'circular',
																variant: 'dot',
															},
														]
													: undefined
											}
											loan={loan}
										/>
									</Grid>
									<Grid item>
										<TypographyCaption id={loan.id} />
										<Typography fontWeight={600}>
											{loan.code ?? `${loan.financier}${loan.num}`}
										</Typography>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						{/* Display name column only when financial traceability is disabled */}
						{!featureFlags['FINANCIAL_TRACEABILITY'] && (
							<Grid item xs={12} lg={2}>
								<RowKeyValue
									label={t('name')}
									value={<Typography component="div">{loan.name || t('na')}</Typography>}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('closing')}
								value={<Time date={loan.dateClosing ? new Date(loan.dateClosing) : null} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('amount')}
								value={<Amount currency={loanCurrency} notation="compact" value={loanAmount} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('undisbursed')}
								value={
									isExtFinancing ? (
										<Typography>{t('na')}</Typography>
									) : (
										<Amount currency={loanCurrency} notation="compact" value={loanUndisbursed} />
									)
								}
							/>
						</Grid>
						{featureFlags['UTILIZATION'] ? (
							<Grid item xs={12} lg={1}>
								<RowKeyValue
									label={t('utilization')}
									value={<ChipUtilization utilization={loan.utilization} />}
								/>
							</Grid>
						) : null}
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('transactionsEvidenced')}
								value={<ChipTraceability traceability={loan.traceability} />}
							/>
						</Grid>
						{featureFlags['FINANCIAL_TRACEABILITY'] && (
							<Grid item xs={12} lg={1}>
								<RowKeyValue
									label={t('financialValueEvidenced')}
									value={<ChipTraceability traceability={loan.traceability} type="FINANCIAL" />}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('status')}
								value={
									<ChipStatus status={project.status === 'PENDING' ? 'PENDING' : loan.status} />
								}
							/>
						</Grid>
						<Grid item xs={12} lg="auto" sx={{ ml: { lg: 'auto' } }}>
							{loan.isNew && userProjectPermissions?.loan?.create ? (
								<Button
									onClick={() => handleOnboardLoan(loan)}
									size="small"
									sx={animatedPulse(true, theme.palette.info.main + '77')}
									variant="outlined">
									{t('onboard')}
								</Button>
							) : (
								<Grid
									container
									justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
									spacing={1}>
									{btnMenuItems.length > 0 && (
										<Grid
											item
											sx={{
												position: 'absolute',
												top: 0,
												right: theme.spacing(1),
												[theme.breakpoints.up('lg')]: {
													position: 'relative',
													top: 'auto',
													right: 'auto',
												},
											}}>
											<BtnMenu items={btnMenuItems} />
										</Grid>
									)}
									<Grid item>
										<BtnExpand
											isExpanded={isExpanded}
											label={isExpanded ? t('hideDetails') : t('seeDetails')}
											onClick={handleToggleCard}
										/>
									</Grid>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="transactions">
							<TableBasicTransactions
								data={transactions}
								isLoading={isLoadingTransactions}
								pagination={paginationTransactions}
							/>
						</TabPanel>
					</TabContext>
				)}
			</Paper>

			{isDisabled && isProjectOnboarded && (
				<DialogEnableLoan
					isOpen={paramDialog === 'enableLoan' && paramDialogLoan === loan.code}
					loan={loan}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchLoans();
						refetchActivities();
					}}
				/>
			)}
			<DialogOnboardLoan
				isOpen={paramDialog === 'onboardLoan' && paramDialogLoan === loan.code}
				loan={loan}
				onClose={handleCloseDialog}
				onSuccess={handleOnboardingSuccess}
				project={project as unknown as ProjectPayload}
			/>
		</>
	);
};
